import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { SvgIconComponent, SvgIcons, injectRegisterIcons } from '@ngneat/svg-icon';
import { Store } from '@ngrx/store';
import { TranslatePipe } from '@ngx-translate/core';

import { getQuickNavigationRoute } from '@core/config/quick-navigation-routes.config';
import { RouteSegment } from '@core/enums/route-segment.enum';
import { selectIsDashboardEnabled, selectIsGlobalChargersEnabled, selectIsGlobalVehiclesEnabled } from '@core/stores/auth/auth.selectors';
import { TenantsStore } from '@core/stores/tenants/tenants.store';

import { PermissionStoreModelEnum } from '@domain/permissions/permissions.enums';
import { FeatureFlags } from '@domain/tenant/tenant.enums';

import { LoadingSkeletonComponent } from '@shared/components/loading-skeleton/loading-skeleton.component';
import { PopoverDirective } from '@shared/modules/popover';
import { HasFeatureFlagsPipe } from '@shared/pipes/has-feature-flags/has-feature-flags.pipe';
import { HasPermissionPipe } from '@shared/pipes/has-permission/has-permission.pipe';

import { appAiInsights24Icon } from '@icons/ai-insights-24';
import { appAlert16Icon } from '@icons/alert-16';
import { appBolt24Icon } from '@icons/bolt-24';
import { appCharger16Icon } from '@icons/charger-16';
import { appChevronLeft40Icon } from '@icons/chevron-left-40';
import { appChevronRight40Icon } from '@icons/chevron-right-40';
import { appDashboard24Icon } from '@icons/dashboard-24';
import { appEnergyTradingIcon } from '@icons/energy-trading';
import { appIssues24Icon } from '@icons/issues-24';
import { appMoney24Icon } from '@icons/money-24';
import { appOnTheRoadV224Icon } from '@icons/on-the-road-v2-24';
import { appReports24Icon } from '@icons/reports-24';
import { appSettings24Icon } from '@icons/settings-24';
import { appSites24Icon } from '@icons/sites-24';
import { appTags24Icon } from '@icons/tags-24';
import { appUsers24Icon } from '@icons/users-24';
import { appVehicles44Icon } from '@icons/vehicles-44';
import { appWorkflow24Icon } from '@icons/workflow-24';

import { SideMenuViewModel } from './side-menu.view-model';

interface SideMenuRoute {
  icon: SvgIcons;
  labelTranslationKey: string;
  route: string[] | null;
  shadowRoute?: string[];
  exact?: boolean;
  featureFlags?: FeatureFlags | FeatureFlags[];
  permissions?: PermissionStoreModelEnum | PermissionStoreModelEnum[];
  hideWhenDashboardDisabled?: boolean;
  hideWhenGlobalChargersDisabled?: boolean;
  hideWhenGlobalVehiclesDisabled?: boolean;
  blockNavigationWhileSiteListIsLoading?: boolean;
}

@Component({
  selector: 'app-side-menu',
  imports: [
    NgClass,
    TranslatePipe,
    SvgIconComponent,
    RouterLink,
    RouterLinkActive,
    HasFeatureFlagsPipe,
    HasPermissionPipe,
    PopoverDirective,
    LoadingSkeletonComponent,
  ],
  templateUrl: './side-menu.component.html',
  styleUrl: './side-menu.component.scss',
  host: {
    class: 'flex flex-col rounded-radius-primary bg-background-menu',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SideMenuComponent {
  readonly #store = inject(Store);
  readonly #tenantsStore = inject(TenantsStore);

  public vm = input.required<SideMenuViewModel>();
  public expandChange = output<boolean>();

  protected readonly isDashboardEnabled = this.#store.selectSignal(selectIsDashboardEnabled);
  protected readonly isGlobalChargersEnabled = this.#store.selectSignal(selectIsGlobalChargersEnabled);
  protected readonly isGlobalVehiclesEnabled = this.#store.selectSignal(selectIsGlobalVehiclesEnabled);
  protected readonly siteListIsInitialLoading = this.#tenantsStore.isInitialLoading;
  protected readonly singleSite = this.#tenantsStore.singleSite;
  protected readonly routes = computed((): SideMenuRoute[] => {
    const singleSite = this.singleSite();
    const dashboardRoute = getQuickNavigationRoute('dashboard');
    const sharingRoute = getQuickNavigationRoute('sharing');
    const roamingRoute = getQuickNavigationRoute('roaming');
    const flexibilityRoute = getQuickNavigationRoute('flexibility');
    const energyTradingRoute = getQuickNavigationRoute('energy-trading');
    const idTagsRoute = getQuickNavigationRoute('id-tags');
    const alertingRoute = getQuickNavigationRoute('alerting');
    const reportsRoute = getQuickNavigationRoute('reports');
    const vehiclesRoute = getQuickNavigationRoute('vehicles');
    const chargersRoute = getQuickNavigationRoute('chargers');
    const workflowRoute = getQuickNavigationRoute('workflow');
    const settingsRoute = getQuickNavigationRoute('settings-users');
    const sitesRoute: SideMenuRoute = singleSite
      ? {
          icon: 'dashboard-24',
          labelTranslationKey: 'side-menu.site',
          route: [RouteSegment.Root, RouteSegment.Sites, singleSite.id, RouteSegment.Overview],
          shadowRoute: [RouteSegment.Root, RouteSegment.Sites],
          blockNavigationWhileSiteListIsLoading: true,
        }
      : {
          icon: 'dashboard-24',
          labelTranslationKey: 'side-menu.sites',
          route: [RouteSegment.Root, RouteSegment.Sites],
          blockNavigationWhileSiteListIsLoading: true,
        };

    return [
      {
        icon: 'sites-24',
        labelTranslationKey: dashboardRoute.labelTranslationKey,
        route: dashboardRoute.segments,
        exact: true,
        hideWhenDashboardDisabled: dashboardRoute.requiresDashboardEnabled,
        permissions: dashboardRoute.permissions,
      },
      sitesRoute,
      {
        icon: 'money-24',
        labelTranslationKey: sharingRoute.labelTranslationKey,
        route: [RouteSegment.Root, RouteSegment.Sharing],
        featureFlags: sharingRoute.featureFlags,
        permissions: sharingRoute.permissions,
      },
      {
        icon: 'on-the-road-v-2-24',
        labelTranslationKey: roamingRoute.labelTranslationKey,
        route: [RouteSegment.Root, RouteSegment.Roaming],
        featureFlags: roamingRoute.featureFlags,
        permissions: roamingRoute.permissions,
      },
      {
        icon: 'bolt-24',
        labelTranslationKey: flexibilityRoute.labelTranslationKey,
        route: flexibilityRoute.segments,
        featureFlags: flexibilityRoute.featureFlags,
        permissions: flexibilityRoute.permissions,
      },
      {
        icon: 'energy-trading',
        labelTranslationKey: energyTradingRoute.labelTranslationKey,
        route: energyTradingRoute.segments,
        permissions: energyTradingRoute.permissions,
      },
      {
        icon: 'tags-24',
        labelTranslationKey: idTagsRoute.labelTranslationKey,
        route: idTagsRoute.segments,
        permissions: idTagsRoute.permissions,
      },

      // TODO - commented side menu items
      /*  {
      icon: 'issues-24',
      labelTranslationKey: 'side-menu.issues',
      route: null,
    },
    {
      icon: 'ai-insights-24',
      labelTranslationKey: 'side-menu.ai-insights',
      route: null,
    }, */
      {
        icon: 'alert-16',
        labelTranslationKey: alertingRoute.labelTranslationKey,
        route: alertingRoute.segments,
      },
      {
        icon: 'reports-24',
        labelTranslationKey: reportsRoute.labelTranslationKey,
        route: reportsRoute.segments,
        permissions: reportsRoute.permissions,
      },
      {
        icon: 'vehicles-44',
        labelTranslationKey: vehiclesRoute.labelTranslationKey,
        route: vehiclesRoute.segments,
        permissions: vehiclesRoute.permissions,
        hideWhenGlobalVehiclesDisabled: vehiclesRoute.requiresGlobalVehiclesEnabled,
        blockNavigationWhileSiteListIsLoading: true,
      },
      {
        icon: 'charger-16',
        labelTranslationKey: chargersRoute.labelTranslationKey,
        route: chargersRoute.segments,
        permissions: chargersRoute.permissions,
        hideWhenGlobalChargersDisabled: chargersRoute.requiresGlobalChargersEnabled,
      },
      {
        icon: 'workflow-24',
        labelTranslationKey: workflowRoute.labelTranslationKey,
        route: workflowRoute.segments,
        permissions: workflowRoute.permissions,
      },
      {
        icon: 'settings-24',
        labelTranslationKey: 'side-menu.settings',
        route: [RouteSegment.Root, RouteSegment.Settings],
        permissions: [
          ...(settingsRoute.permissions ?? []),
          PermissionStoreModelEnum.ViewAnyAuditLogs,
          PermissionStoreModelEnum.ViewOwnAuditLogs,
        ],
      },
      /* {
      icon: 'users-24',
      labelTranslationKey: 'side-menu.users',
      route: null,
    },
    {
      icon: 'settings-24',
      labelTranslationKey: 'side-menu.settings',
      route: null,
    }, */
    ];
  });

  constructor() {
    injectRegisterIcons([
      appDashboard24Icon,
      appSites24Icon,
      appTags24Icon,
      appIssues24Icon,
      appAiInsights24Icon,
      appAlert16Icon,
      appReports24Icon,
      appUsers24Icon,
      appSettings24Icon,
      appChevronLeft40Icon,
      appChevronRight40Icon,
      appOnTheRoadV224Icon,
      appMoney24Icon,
      appBolt24Icon,
      appEnergyTradingIcon,
      appCharger16Icon,
      appWorkflow24Icon,
      appVehicles44Icon,
    ]);
  }

  public onExpandToggle(): void {
    this.expandChange.emit(!this.vm().expanded);
  }
}
